"use client";

import { useQuery } from "@tanstack/react-query";

import { useEffect, useRef, useState } from "react";

import { useParams } from "next/navigation";

import { useCarePlanContext } from "@/components/care-plan/care-plan";
import { CarePlanEditorLayout } from "@/components/care-plan/care-plan-editor-layout";
import { instantiateTemplate } from "@/components/care-plan/instantiate-template";
import {
  type PatientCarePlanCreateInput,
  PatientCarePlanCreator,
} from "@/components/care-plan/patient-care-plan-creator";
import { StepList } from "@/components/care-plan/step-list";
import {
  type CarePlanEditor,
  type CarePlanSaveState,
  type PatientCarePlanStep,
} from "@/components/care-plan/types";
import { useProgressNavigation } from "@/components/progress-bar";
import { Button, Label, Textarea } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { Skeleton } from "@/components/ui/skeleton";
import { SmartDateInput } from "@/components/ui/smart-date-input";

import { useUser } from "@/contexts/user-provider";

import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useLocations } from "@/hooks/use-locations";

import { clinicDayMarker, pickedDay } from "@/lib/clinic-clock";

import type { CarePlanTemplateDetail } from "@/types/care-plan-template";

import { carePlanTemplateQueryOptions } from "@/api/provider/settings/care-plan-template-query";

export const SCRATCH_TEMPLATE_ID = "new";

// ============================================================================
// Main Page — waits for template, then renders editor
// ============================================================================

export default function ConfigureCarePlanPage() {
  const params = useParams<{ id: string; templateId: string }>();
  const { navigateWithProgress } = useProgressNavigation();

  const isScratch = params.templateId === SCRATCH_TEMPLATE_ID;
  const pickerUrl = `/patient/${params.id}/care-plan/new`;

  const {
    data: template,
    isPending,
    isError,
  } = useQuery({
    ...carePlanTemplateQueryOptions.detail(params.templateId),
    enabled: !isScratch,
  });

  if (!isScratch && isPending) {
    return (
      <div className="w-full mx-auto space-y-2 p-4 md:p-6 h-full">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isScratch && (isError || !template)) {
    return (
      <div className="flex flex-col items-center justify-center p-16 text-center">
        <p className="text-h3 font-medium text-foreground">
          Care plan template not found
        </p>
        <Button
          className="mt-4"
          variant="secondary"
          onClick={() => navigateWithProgress(pickerUrl)}
        >
          Back to Care Plan Templates
        </Button>
      </div>
    );
  }

  return <CarePlanConfigurator template={isScratch ? undefined : template!} />;
}

// ============================================================================
// Configurator — mounts only after template is resolved
// ============================================================================

function CarePlanConfigurator({
  template,
}: {
  template?: CarePlanTemplateDetail;
}) {
  const params = useParams<{ id: string; templateId: string }>();
  const { navigateWithProgress } = useProgressNavigation();

  const isScratch = !template;
  const patientUrl = `/patient/${params.id}`;

  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [tagIds, setTagIds] = useState<string[]>(template?.tagIds ?? []);
  const [tagError, setTagError] = useState(false);
  const [isTagSaving, setIsTagSaving] = useState(false);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const prevStartDateRef = useRef<Date>(startDate);
  // The start field reads its day off the clinic clock as it is now.
  const clock = useClinicClock();
  const [steps, setSteps] = useState<PatientCarePlanStep[]>(() => {
    if (!template?.steps) return [];
    return instantiateTemplate(template.steps, startDate);
  });

  // Shift step dates by delta when start date changes, preserving user edits
  useEffect(() => {
    const prevDate = prevStartDateRef.current;
    if (prevDate.getTime() === startDate.getTime()) return;
    const deltaMs = startDate.getTime() - prevDate.getTime();
    prevStartDateRef.current = startDate;
    setSteps((prev) =>
      prev.map((step) => {
        if (!step.scheduledDate) return step;
        const shifted = new Date(
          new Date(step.scheduledDate).getTime() + deltaMs,
        );
        return { ...step, scheduledDate: shifted.toISOString() };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate.getTime()]);

  const editor: CarePlanEditor = {
    name,
    onNameChange: (value) => setName(value),
    namePlaceholder: "e.g. Weight Loss",
    nameAutoFocus: isScratch,
    description,
    onDescriptionChange: (value) => setDescription(value),
    descriptionInput: (
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Brief description of this care plan..."
        rows={2}
      />
    ),
    headerRight: (
      <div>
        <Label className="text-caption text-foreground-secondary">
          Start Date
        </Label>
        <SmartDateInput
          value={clinicDayMarker(startDate, clock)}
          onChange={(date) => {
            // The plan starts on the picked clinic day, at the time it
            // already held, so its steps stay on clinic days too.
            if (date) {
              setStartDate(
                clock.at(pickedDay(date), clock.wallMinutes(startDate)),
              );
            }
          }}
          placeholder="Select start date..."
        />
      </div>
    ),
    selectedTagIds: tagIds,
    onTagsChange: (ids) => {
      setTagIds(ids);
      if (ids.length > 0) setTagError(false);
    },
    onTagSavingChange: setIsTagSaving,
    tagError,
    tagErrorMessage: "At least one tag is required",
  };

  return (
    <div className="w-full space-y-2 p-4 md:p-6 h-full">
      <PatientCarePlanCreator
        patientId={params.id}
        steps={steps}
        setSteps={setSteps}
        startDate={startDate}
        editor={editor}
        templateId={isScratch ? undefined : params.templateId}
        templateCycles={template?.cycles}
      >
        <CarePlanEditorLayout
          footer={
            <AssignFooter
              onCancel={() => navigateWithProgress(patientUrl)}
              isTagSaving={isTagSaving}
            />
          }
        >
          <StepList />
        </CarePlanEditorLayout>
      </PatientCarePlanCreator>
    </div>
  );
}

function AssignFooter({
  onCancel,
  isTagSaving,
}: {
  onCancel: () => void;
  isTagSaving: boolean;
}) {
  const ctx = useCarePlanContext();
  const saveState = ctx.saveState as
    | CarePlanSaveState<PatientCarePlanCreateInput>
    | undefined;
  const { navigateWithProgress } = useProgressNavigation();
  const { currentLocationId, updatePrimaryLocation } = useUser();
  const { locations } = useLocations();
  const params = useParams<{ id: string }>();

  const isSaving = saveState?.isSaving ?? false;
  const validationError = saveState?.validationError;
  const hasLocation = !!currentLocationId;
  const isDisabled = !!validationError || isSaving || isTagSaving;

  const submitCarePlan = async (locationId: string) => {
    if (!saveState?.create) return;
    const success = await saveState.create({ locationId });
    if (success) navigateWithProgress(`/patient/${params.id}`);
  };

  const handleLocationSelect = async (locationId: string) => {
    // Update the primary location preference without blocking the main action
    updatePrimaryLocation(locationId).catch((error) => {
      console.error("Failed to update primary location:", error);
    });
    await submitCarePlan(locationId);
  };

  const assignLabel = isSaving ? "Assigning..." : "Assign Care Plan";

  if (!hasLocation && locations.length > 0) {
    return (
      <>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isDisabled}>
            <Button
              variant="primary"
              disabled={isDisabled}
              tooltip={validationError}
            >
              {assignLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Select a location</DropdownMenuLabel>
            {locations.map((location) => (
              <DropdownMenuItem
                key={location.id}
                onClick={() => handleLocationSelect(location.id)}
              >
                {location.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }

  return (
    <>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        onClick={() => currentLocationId && submitCarePlan(currentLocationId)}
        variant="primary"
        disabled={isDisabled || !hasLocation}
        tooltip={validationError}
      >
        {assignLabel}
      </Button>
    </>
  );
}
